// Lifted from web + mobile MaxesContext (ALIGNMENT_AUDIT HIGH 1, now next/archive/ALIGNMENT_AUDIT.md).
// Both surfaces deserialised `/api/me/maxes` with the exact same loop
// (Max.safeParse → Map<exerciseId, valueKg>); a divergence here would
// produce silently different MAX maps on web vs mobile, so the read is
// canonical and lives in `@heraion/shared`.

import type { ApiGet } from "./apiFetch.js";
import { Max, MaxRead } from "./schemas/max.js";
import type { CurrentMax } from "./maxRegression.js";

/**
 * Minimal contract the surface-side `apiFetch` must honour. Web and
 * mobile each add their own option bags (`ApiOptions`, retry policy,
 * Idempotency-Key); none of that matters for a plain GET, so the lifted
 * helper only requires a path-in / parsed-JSON-out shape.
 */
export type ApiFetcher = ApiGet;

// CO-11 coach read-through: an optional athleteSub routes the GET at an
// assigned athlete's MAXes (edge-gated server-side). Self reads pass none.
const maxesPath = (athleteSub?: string): string =>
  athleteSub
    ? `/api/me/maxes?athleteSub=${encodeURIComponent(athleteSub)}`
    : "/api/me/maxes";

/**
 * Fetches `/api/me/maxes` and deserialises into a `Map<exerciseId, kg>`.
 *
 * Skips rows that fail `Max.safeParse` — matches the previous in-line
 * web + mobile behaviour: a single bogus row in the response shouldn't
 * blow up the entire MAX map. Storage stays canonical kg; the consumer
 * formats for display.
 */
export const fetchMaxesMap = async (
  apiFetch: ApiFetcher,
  athleteSub?: string,
): Promise<Map<string, number>> => {
  const data = await apiFetch<unknown>(maxesPath(athleteSub));
  const arr = Array.isArray(data) ? data : [];
  const next = new Map<string, number>();
  for (const raw of arr) {
    const p = Max.safeParse(raw);
    if (p.success) next.set(p.data.exerciseId, p.data.valueKg);
  }
  return next;
};

/**
 * Fetches `/api/me/maxes` and deserialises into a `Map<exerciseId,
 * {valueKg, setAt}>`. Twin of `fetchMaxesMap` that KEEPS the `setAt`
 * timestamp — the bidirectional MAX-regression check
 * (detectMaxRegression) needs it for the staleness gate, while the
 * legacy callers only want kg. Same row-skip-on-parse-failure behaviour.
 * The kg-only map can be derived from this (`new Map([...m].map(([k, v])
 * => [k, v.valueKg]))`) so a surface that needs both fetches once.
 */
/** A MAX as a coach reads it: `setAt` is absent when it predates the bond (#333). */
export type CoachCurrentMax = Omit<CurrentMax, "setAt"> & { setAt?: string };

export const fetchMaxesDetailed = async (
  apiFetch: ApiFetcher,
  athleteSub?: string,
): Promise<Map<string, CoachCurrentMax>> => {
  const data = await apiFetch<unknown>(maxesPath(athleteSub));
  const arr = Array.isArray(data) ? data : [];
  const next = new Map<string, CoachCurrentMax>();
  for (const raw of arr) {
    const p = MaxRead.safeParse(raw);
    if (p.success) {
      next.set(p.data.exerciseId, {
        valueKg: p.data.valueKg,
        setAt: p.data.setAt,
        // Carried, not dropped: a reader that PROPOSES a load off this value
        // has to know whether it was tested or merely seeded at import
        // (isMaxTrustedForProposal). The regression check ignores it.
        source: p.data.source,
      });
    }
  }
  return next;
};

/**
 * Narrows a detailed read to the rows that carry their date, for the self
 * callers (regression check, staleness gate) that need `setAt`. A row
 * without one is left out, never given an invented date.
 */
export const datedMaxes = (
  m: ReadonlyMap<string, CoachCurrentMax>,
): Map<string, CurrentMax> => {
  const out = new Map<string, CurrentMax>();
  for (const [slug, v] of m) {
    if (v.setAt !== undefined) out.set(slug, { ...v, setAt: v.setAt });
  }
  return out;
};

// PG-59 PR-timeline widget: `/api/me/maxes?include=history` returns the
// current MAX per exercise plus its history, oldest first
// (apps/api/src/routes/maxes.ts include=history branch). One item per
// exercise; PUT and /batch both write current+history in one transaction, so
// an empty history array means the row predates history tracking.
// `current` is the read shape: a coach sees it undated when it predates the
// bond, while its `history` rows all start at the bond and keep their date.
export type MaxHistoryItem = { current: MaxRead; history: Max[] };

const maxesHistoryPath = (athleteSub?: string): string =>
  athleteSub
    ? `/api/me/maxes?include=history&athleteSub=${encodeURIComponent(athleteSub)}`
    : "/api/me/maxes?include=history";

/**
 * Fetches `/api/me/maxes?include=history` and parses every `current` +
 * `history` row through `Max.safeParse`, same skip-on-drift behaviour as
 * `fetchMaxesMap` / `fetchMaxesDetailed`: a row that fails validation is
 * dropped rather than blowing up the whole read. An item whose `current`
 * itself fails to parse is dropped entirely (nothing left to anchor its
 * history to).
 */
export const fetchMaxesHistory = async (
  apiFetch: ApiFetcher,
  athleteSub?: string,
): Promise<MaxHistoryItem[]> => {
  const data = await apiFetch<{ items?: unknown[] }>(
    maxesHistoryPath(athleteSub),
  );
  const arr = Array.isArray(data.items) ? data.items : [];
  const items: MaxHistoryItem[] = [];
  for (const raw of arr) {
    if (typeof raw !== "object" || raw === null) continue;
    const entry = raw as { current?: unknown; history?: unknown };
    const current = MaxRead.safeParse(entry.current);
    if (!current.success) continue;
    const historyArr = Array.isArray(entry.history) ? entry.history : [];
    const history: Max[] = [];
    for (const h of historyArr) {
      const p = Max.safeParse(h);
      if (p.success) history.push(p.data);
    }
    items.push({ current: current.data, history });
  }
  return items;
};

